import type ApolloClient from 'apollo-client'
import { getOperationName } from 'apollo-link'
import { DealDocument } from '@paintscout/api'
import { refetchActiveQueriesByName } from './scheduleDelayedRefetch'

/**
 * Offsets, in ms from the send, at which the `deal` query is refetched.
 *
 * A single delayed refetch is not enough here. Sending an estimate can move the deal's stage
 * through an automation, and that runs in a separate Lambda: `sendQuote` calls `processEventAsync`
 * (`apps/api/src/services/integration/process-event-async/process-event-async.ts:21`) with
 * `InvocationType: 'Event'`, so the mutation resolves before the automation has even started. The
 * automation then runs behind integrations and webhooks, and finishes with a conflict-retry loop
 * (`apps/api/src/services/automations/process-automation.ts:148`). There is no completion signal
 * back to the client, so the only option is to look again a few times.
 *
 * Backed off rather than evenly spaced: most runs land early, and the late attempts cover a cold
 * Lambda. Four small `deal` queries spread over 14s, after a rare and deliberate user action.
 */
const RETRY_OFFSETS_MS = [2000, 5000, 9000, 14000]

/**
 * Re-reads the deal a few times after a send, so an automated stage change appears on the estimate
 * without a page reload.
 *
 * Refetching the mounted `deal` query is what drives the UI: `EditableQuote` holds the deal in
 * local state and adopts each incoming copy through `shouldAdoptDeal`, which accepts anything at or
 * ahead of the revision it already has. The automation bumps `_rev`, so the new stage is adopted
 * and `PipelineProgressBar` re-renders. (ENG-1116 QA)
 *
 * Callers should skip this when the send cannot trigger the automation: an estimate with no
 * `deal_id`, or a work-order send, which emits no `quote-status-changed` event.
 *
 * Known limitation: if the automation takes longer than the last offset, the stage stays stale
 * until something else refetches the deal. The window is a trade-off against polling forever, not a
 * guarantee.
 */
export function scheduleAutomatedDealStageSync(client: ApolloClient<any>): void {
  const dealQueryName = getOperationName(DealDocument)

  if (!dealQueryName) {
    throw new Error('scheduleAutomatedDealStageSync: could not resolve the deal operation name')
  }

  RETRY_OFFSETS_MS.forEach((delay, index) => {
    setTimeout(() => {
      // Warn only on the last attempt. A miss on the earlier ones is normal — the user can close
      // the estimate mid-window — and warning four times would bury the signal it exists to give.
      refetchActiveQueriesByName(client, [dealQueryName], { warnOnMiss: index === RETRY_OFFSETS_MS.length - 1 })
    }, delay)
  })
}
